// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { compose } from 'recompose';
import { memoize } from 'lodash';

// Components
import userReFetchDecorator from '../../user/userReFetchDecorator';
import Icon from '../icons/Icon';
import AvatarImage from '../images/AvatarImage';

// Util
import { prop } from '../../../common/utils/immutableHelper';
import { getInitials } from '../../user/userUtil';
import { scaleToGrid } from '../../utils/grid/gridUtils';
import { getIsAnonymousProp, getIsGuestProp, getUserImage } from '../../../common/users/utils/userPropertyUtils';
import { getEmail, getGivenName, getId } from '../../../common/users/userHelper';
import { objectIdCreationSeconds } from '../../../common/utils/objectIdUtil';

// Selectors
import { getGridSize } from '../../utils/grid/gridSizeSelector';

// Styles
import './UserAvatar.scss';

const AVATAR_TYPES = {
    GUEST: 'guest',
    IMAGE: 'image',
    INITIALS: 'initials',
};

export const getAvatarStyle = memoize((sizePx) => ({
    '--avatar-size': `${sizePx}px`,
}));

const getAvatarType = ({ user, attachment }) => {
    if (!user || getIsGuestProp(user) || getIsAnonymousProp(user)) return AVATAR_TYPES.GUEST;

    if (getUserImage(user) || (attachment && attachment.size)) return AVATAR_TYPES.IMAGE;

    if (getEmail(user) && !getGivenName(user)) return AVATAR_TYPES.GUEST;

    return AVATAR_TYPES.INITIALS;
};

const getAvatarColorClass = (user) => {
    const userAvatarColorIndex = objectIdCreationSeconds(getId(user)) % 8;
    return `avatar-color-${userAvatarColorIndex}`;
};

const mapStateToProps = createSelector(
    getGridSize,
    (state, ownProps) => ownProps.size,
    (gridSize, size) => ({
        sizePx: Math.ceil(scaleToGrid(size, gridSize)),
    }),
);

const GuestAvatar = () => (
    <span className="GuestAvatar">
        <Icon name="user-guest" />
    </span>
);

const UserAvatarType = ({ user, sizePx, attachment, onImageLoadCb, type }) => {
    switch (type) {
        case AVATAR_TYPES.IMAGE:
            return (
                <div className="avatar-contents">
                    <AvatarImage attachment={attachment} user={user} sizePx={sizePx} onImageLoadCb={onImageLoadCb} />
                </div>
            );
        case AVATAR_TYPES.INITIALS:
            return (
                <div className={classNames('avatar-contents', getAvatarColorClass(user))}>
                    <span className="initals">{getInitials(user)}</span>
                </div>
            );
        case AVATAR_TYPES.GUEST:
        default:
            return (
                <div className="avatar-contents">
                    <GuestAvatar />
                </div>
            );
    }
};

UserAvatarType.propTypes = {
    type: PropTypes.string,
    user: PropTypes.object,
    active: PropTypes.bool,
    sizePx: PropTypes.number.isRequired,
    attachment: PropTypes.object,
    onImageLoadCb: PropTypes.func,
};

const UserAvatar = (props) => {
    const { user, active, activeIndicator, sizePx, attachment } = props;
    const hasImage = prop('image', user) || (attachment && attachment.size);

    const indicator = activeIndicator ? <Icon name="notification-live" /> : null;

    const type = getAvatarType(props);

    return (
        <div
            className={classNames('UserAvatar', type, { active, 'has-image': hasImage })}
            style={getAvatarStyle(sizePx)}
        >
            <UserAvatarType {...props} type={type} />
            {indicator}
        </div>
    );
};

const enhancer = compose(connect(mapStateToProps), userReFetchDecorator);

UserAvatar.propTypes = {
    user: PropTypes.object,
    active: PropTypes.bool,
    activeIndicator: PropTypes.bool,
    size: PropTypes.number.isRequired,
    sizePx: PropTypes.number,
    attachment: PropTypes.object,
    onImageLoadCb: PropTypes.func,
};

// This provides the same functionality as the UserAvatar without the auto-fetching of the user-data
export const SimpleUserAvatar = connect(mapStateToProps)(UserAvatar);

export default enhancer(UserAvatar);
